/**
 * Oxbo-P client — the operational + PLAN store. Unlike Oxbo-Z (read-through),
 * Oxbo-W WRITES the plan slice: it creates/versions plans, edits the design
 * datasets per version, requests supplements, and publishes the plan of record
 * (→ Oxbo-Z baseline + → Aura secuencia_operativa).
 *
 * Endpoints mirror the §4 plan-write/publish contract (see
 * oxbo-w-zsplit-and-endpoints.md). MSW serves mocks in standalone mode; every
 * shape matches the real service so VITE_USE_MOCKS=false is a drop-in swap.
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  EventOut,
  PlanOut,
  PlanCreate,
  PlanStatus,
  SupplementOut,
  SupplementStatus,
  PlanDatasets,
  ProgramaStep,
  HolePlanRow,
  CasingRow,
  AttributeRow,
  AfeLineRow,
  TrajectoryStationInput,
  TrajectoryStationOut,
  PlanTimelineOut,
  TimelineNode,
  PublishPreviewOut,
  PublishResultOut,
} from "./types";
import { asArray } from "@lib/asArray";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/** Datasets that can be written per plan version (each a dedicated route). */
export type PlanDatasetKind =
  | "programa"
  | "hole-plan"
  | "casing"
  | "objetivo"
  | "atributos"
  | "afe-lines";

export const pApi = createApi({
  reducerPath: "pApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${P_BASE_URL}/v1` }),
  keepUnusedDataFor: 60,
  tagTypes: ["Event", "Plan", "PlanList", "Datasets", "Timeline", "Supplement", "Publish"],
  endpoints: (builder) => ({
    // ── Events (jobs) on a well — the finder + workspace context ────────────
    getWellEvents: builder.query<EventOut[], string>({
      query: (wellUwi) => `/wells/${encodeURIComponent(wellUwi)}/events`,
      providesTags: (result, _e, wellUwi) => [
        // asArray: a real backend can return a non-array body ({} / error
        // envelope); a truthy `if (result)` guard would let `.map` throw here
        // and wedge the query in isLoading (Oxbo-Z crash class).
        ...asArray<EventOut>(result).map((ev) => ({ type: "Event" as const, id: ev.id })),
        { type: "Event" as const, id: `WELL-${wellUwi}` },
      ],
    }),

    // ── Plan lifecycle & versioning (D1) ────────────────────────────────────
    getEventPlans: builder.query<PlanOut[], string>({
      query: (eventId) => `/events/${eventId}/plans`,
      providesTags: (result, _e, eventId) => [
        // asArray guards the same non-array-body crash class as getWellEvents.
        ...asArray<PlanOut>(result).map((p) => ({ type: "Plan" as const, id: p.id })),
        { type: "PlanList" as const, id: `EVENT-${eventId}` },
      ],
    }),
    getPlan: builder.query<PlanOut, string>({
      query: (planId) => `/plans/${planId}`,
      providesTags: (_r, _e, id) => [{ type: "Plan", id }],
    }),
    createPlan: builder.mutation<PlanOut, { eventId: string; body: PlanCreate }>({
      query: ({ eventId, body }) => ({
        url: `/events/${eventId}/plans`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [{ type: "PlanList", id: `EVENT-${eventId}` }],
    }),
    // New version — append-only; supersedes the prior version.
    createPlanVersion: builder.mutation<PlanOut, string>({
      query: (planId) => ({ url: `/plans/${planId}/versions`, method: "POST" }),
      invalidatesTags: (result) =>
        result ? [{ type: "PlanList", id: `EVENT-${result.event_id}` }] : [],
    }),
    // Status transition (draft→review→approved→locked); approval engine (D18/D24).
    setPlanStatus: builder.mutation<PlanOut, { planId: string; status: PlanStatus }>({
      query: ({ planId, status }) => ({
        url: `/plans/${planId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, _e, { planId }) =>
        result
          ? [
              { type: "Plan", id: planId },
              { type: "PlanList", id: `EVENT-${result.event_id}` },
            ]
          : [{ type: "Plan", id: planId }],
    }),

    // ── AFE supplements (D24) ───────────────────────────────────────────────
    getPlanSupplements: builder.query<SupplementOut[], string>({
      query: (planId) => `/plans/${planId}/supplements`,
      providesTags: (_r, _e, planId) => [{ type: "Supplement", id: planId }],
    }),
    createSupplement: builder.mutation<
      SupplementOut,
      { planId: string; amount: number; reason: string }
    >({
      query: ({ planId, amount, reason }) => ({
        url: `/plans/${planId}/supplements`,
        method: "POST",
        body: { amount, reason },
      }),
      invalidatesTags: (_r, _e, { planId }) => [
        { type: "Supplement", id: planId },
        { type: "Plan", id: planId },
      ],
    }),
    // Advance a supplement through its approval trail (approve / reject a step);
    // approving the final step flips the supplement to `approved` and it then
    // counts toward the authorized total (server-authoritative).
    setSupplementStatus: builder.mutation<
      SupplementOut,
      { planId: string; supplementId: string; status: SupplementStatus }
    >({
      query: ({ supplementId, status }) => ({
        url: `/supplements/${supplementId}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { planId }) => [
        { type: "Supplement", id: planId },
        { type: "Plan", id: planId },
      ],
    }),

    // ── Plan datasets (per version) ─────────────────────────────────────────
    getPlanDatasets: builder.query<PlanDatasets, string>({
      query: (planId) => `/plans/${planId}/datasets`,
      providesTags: (_r, _e, planId) => [{ type: "Datasets", id: planId }],
    }),
    putPrograma: builder.mutation<ProgramaStep[], { planId: string; rows: ProgramaStep[] }>({
      query: ({ planId, rows }) => ({
        url: `/plans/${planId}/programa`,
        method: "PUT",
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),
    putHolePlan: builder.mutation<HolePlanRow[], { planId: string; rows: HolePlanRow[] }>({
      query: ({ planId, rows }) => ({
        url: `/plans/${planId}/hole-plan`,
        method: "PUT",
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),
    putCasing: builder.mutation<CasingRow[], { planId: string; rows: CasingRow[] }>({
      query: ({ planId, rows }) => ({
        url: `/plans/${planId}/casing`,
        method: "PUT",
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),
    putAttributes: builder.mutation<
      AttributeRow[],
      { planId: string; kind: "objetivo" | "atributos"; rows: AttributeRow[] }
    >({
      query: ({ planId, kind, rows }) => ({
        url: `/plans/${planId}/${kind}`,
        method: "PUT",
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),
    // §10.7 afe-lines with plan-column write scoping (W owns plan cols only).
    putAfeLines: builder.mutation<AfeLineRow[], { planId: string; rows: AfeLineRow[] }>({
      query: ({ planId, rows }) => ({
        url: `/plans/${planId}/afe-lines`,
        method: "PUT",
        body: { rows },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),
    // Server computes min-curvature TVD/N-E/DLS from the stations we send.
    putTrajectory: builder.mutation<
      TrajectoryStationOut[],
      { planId: string; stations: TrajectoryStationInput[] }
    >({
      query: ({ planId, stations }) => ({
        url: `/plans/${planId}/trajectory`,
        method: "PUT",
        body: { stations },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Datasets", id: planId }],
    }),

    // ── Plan timeline (W7) — flat node contract (§10.9 / §16e) ──────────────
    // The multi-track Gantt over a plan's scope. `plan` nodes are authored here
    // and PUT back as one stream; `actual`/`npt` overlay nodes are read-only.
    getPlanTimeline: builder.query<PlanTimelineOut, string>({
      query: (planId) => `/plans/${planId}/plan-timeline`,
      providesTags: (_r, _e, planId) => [{ type: "Timeline", id: planId }],
    }),
    // Persist the authored plan bars (re-sequenced / re-timed). We send the full
    // plan-node set; the server keeps actual/npt overlays authoritative.
    putPlanTimeline: builder.mutation<
      PlanTimelineOut,
      { planId: string; nodes: TimelineNode[] }
    >({
      query: ({ planId, nodes }) => ({
        url: `/plans/${planId}/plan-timeline`,
        method: "PUT",
        body: { nodes },
      }),
      invalidatesTags: (_r, _e, { planId }) => [{ type: "Timeline", id: planId }],
    }),

    // ── Publish (the W→Z + W→Aura seam) ─────────────────────────────────────
    getPublishPreview: builder.query<PublishPreviewOut, string>({
      query: (planId) => `/plans/${planId}/publish-preview`,
      providesTags: (_r, _e, planId) => [{ type: "Publish", id: planId }],
    }),
    publishPlan: builder.mutation<PublishResultOut, string>({
      query: (planId) => ({ url: `/plans/${planId}/publish`, method: "POST" }),
      invalidatesTags: (result, _e, planId) =>
        result
          ? [
              { type: "Plan", id: planId },
              { type: "Publish", id: planId },
              { type: "PlanList", id: `EVENT-${result.plan_id}` },
            ]
          : [{ type: "Plan", id: planId }],
    }),
  }),
});

export const {
  useGetWellEventsQuery,
  useGetEventPlansQuery,
  useGetPlanQuery,
  useCreatePlanMutation,
  useCreatePlanVersionMutation,
  useSetPlanStatusMutation,
  useGetPlanSupplementsQuery,
  useCreateSupplementMutation,
  useSetSupplementStatusMutation,
  useGetPlanDatasetsQuery,
  usePutProgramaMutation,
  usePutHolePlanMutation,
  usePutCasingMutation,
  usePutAttributesMutation,
  usePutAfeLinesMutation,
  usePutTrajectoryMutation,
  useGetPlanTimelineQuery,
  usePutPlanTimelineMutation,
  useGetPublishPreviewQuery,
  usePublishPlanMutation,
} = pApi;
